/**
 * HierarchicalIdentity — derives a context-specific Ed25519 sub-key from a
 * single root seed via deterministic KDF (Key Derivation Function).
 *
 * MODEL:
 *
 *   root_seed ──Argon2id──► root_secret (32 bytes, never used to sign directly)
 *
 *   per context C:
 *     ctx_hash    = BLAKE3(canonical(C))
 *     ctx_secret  = HKDF(root_secret, salt=ctx_hash, info="surface-mesh:v1:ctx", L=32)
 *     ctx_pubkey  = Ed25519.derivePub(ctx_secret)
 *
 * Two contexts with different (environment, purpose, epoch) tuples produce
 * cryptographically independent keypairs. A network observer sees N distinct
 * pubkeys for N contexts, with NO ability to link them to a common root.
 *
 * LINKAGE: the user can OPT IN to proving two context pubkeys share a root
 * by producing a Schnorr-style zero-knowledge proof of common discrete-log
 * preimage. The proof is reusable for reputation portability without giving
 * the network observer continuous linkage.
 *
 * TRADE-OFFS vs Global:
 *   ✓ Unlinkability across environments
 *   ✓ Context compromise doesn't leak other context history
 *   ✓ Selective disclosure of linkage
 *   ✗ Recovery UX: derivation rules must be reproducible across devices
 *   ✗ Reputation portability requires explicit proof construction
 *   ✗ More complex audit trail (one user appears as many actors)
 *   ✗ Per-context key cache management
 */

import { ed25519 } from '@noble/curves/ed25519';
import { blake3 } from '@noble/hashes/blake3';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex, utf8ToBytes } from '@noble/hashes/utils';
import { base58 } from '@scure/base';

import type {
  Bytes,
  Context,
  ContextHash,
  IdentityFactory,
  LinkageProof,
  PublicKey,
  Signature,
  SignerIdentity,
} from './SignerIdentity';

// ─────────────────────────────────────────────────────────────────────────────
// CANONICAL CONTEXT HASHING
// ─────────────────────────────────────────────────────────────────────────────
// CRITICAL: the context hash MUST be canonical. Two clients computing the
// hash for "the same context" must arrive at identical bytes, or they'll
// derive different sub-keys and signatures won't verify across devices.

function canonicalContext(ctx: Context): string {
  // Sorted-key JSON. epoch may be omitted; treat as 0 when absent.
  const epoch = ctx.epoch ?? 0;
  return JSON.stringify({
    environment: ctx.environment,
    purpose: ctx.purpose,
    epoch,
  }, ['environment', 'epoch', 'purpose']);
}

function contextHash(ctx: Context): Bytes {
  return blake3(utf8ToBytes('surface-mesh:v1:ctx:' + canonicalContext(ctx)));
}

function contextHashHex(ctx: Context): ContextHash {
  return bytesToHex(contextHash(ctx));
}

// ─────────────────────────────────────────────────────────────────────────────
// ROOT SECRET DERIVATION
// ─────────────────────────────────────────────────────────────────────────────

// Phase 19.1: WASM argon2id via hash-wasm. OWASP-recommended params
// (t=3, m=64MB) run in <3s on a 2024 device, vs >120s with the pure-JS impl.
const ARGON2_PARAMS = {
  iterations: 3,
  memorySize: 64 * 1024,
  parallelism: 1,
  hashLength: 32,
} as const;

async function deriveRootSecret(seed: string, password: string): Promise<Bytes> {
  const { argon2id } = await import('hash-wasm');
  const salt = blake3(utf8ToBytes(`surface-mesh-hierarchical:v1:${seed}`));
  const out = await argon2id({
    password,
    salt,
    ...ARGON2_PARAMS,
    outputType: 'binary',
  });
  return hkdf(sha256, out, salt, utf8ToBytes('hierarchical-identity-root'), 32);
}

function deriveContextSecret(rootSecret: Bytes, ctx: Context): Bytes {
  const ctxSalt = contextHash(ctx);
  return hkdf(sha256, rootSecret, ctxSalt, utf8ToBytes('surface-mesh:v1:ctx-secret'), 32);
}

function generateSeedPhrase(): string {
  return bytesToHex(ed25519.utils.randomPrivateKey());
}

// ─────────────────────────────────────────────────────────────────────────────
// IMPLEMENTATION
// ─────────────────────────────────────────────────────────────────────────────

class HierarchicalIdentityImpl implements SignerIdentity {
  /** Root secret. NEVER used to sign directly. */
  #rootSecret: Bytes | null;
  /** Cache of derived context keys. Bounded to prevent memory growth. */
  #ctxCache = new Map<ContextHash, { secret: Bytes; pubkey: PublicKey }>();
  /** Stable root fingerprint, computed once at unlock. */
  readonly #rootFingerprint: string;

  /** Max cached context keys before LRU eviction. */
  static readonly CTX_CACHE_MAX = 64;

  constructor(rootSecret: Bytes) {
    this.#rootSecret = rootSecret;
    // Fingerprint is BLAKE3(rootSecret) truncated; never exposed cryptographically.
    // Used only as a stable local label.
    this.#rootFingerprint = base58.encode(blake3(rootSecret).slice(0, 6));
  }

  #materialize(ctx: Context): { secret: Bytes; pubkey: PublicKey } {
    if (!this.#rootSecret) {
      throw new Error('Identity is locked.');
    }
    const ctxHex = contextHashHex(ctx);
    const cached = this.#ctxCache.get(ctxHex);
    if (cached) return cached;

    // LRU bound check
    if (this.#ctxCache.size >= HierarchicalIdentityImpl.CTX_CACHE_MAX) {
      const oldestKey = this.#ctxCache.keys().next().value;
      if (oldestKey !== undefined) {
        const old = this.#ctxCache.get(oldestKey);
        old?.secret.fill(0);
        this.#ctxCache.delete(oldestKey);
      }
    }

    const secret = deriveContextSecret(this.#rootSecret, ctx);
    const pubkey = ed25519.getPublicKey(secret);
    const entry = { secret, pubkey };
    this.#ctxCache.set(ctxHex, entry);
    return entry;
  }

  async sign(payload: Bytes, context: Context): Promise<Signature> {
    const { secret } = this.#materialize(context);
    // Context-specific key signs. Different contexts → different signatures
    // → no observable linkage on the wire.
    return ed25519.sign(payload, secret);
  }

  async verify(
    payload: Bytes,
    signature: Signature,
    _context: Context,
    pubkey: PublicKey,
  ): Promise<boolean> {
    return ed25519.verify(signature, payload, pubkey);
  }

  async pubkey(context: Context): Promise<PublicKey> {
    const { pubkey } = this.#materialize(context);
    return pubkey;
  }

  /**
   * Produce a zero-knowledge proof that two context pubkeys share a common
   * root.
   *
   * ALPHA: this is a placeholder Schnorr proof of knowledge of equal
   * discrete logs. The structure is correct but a production implementation
   * needs careful Fiat-Shamir transform and constant-time scalar arithmetic.
   *
   * TODO(beta): swap to an audited zk-proof library (e.g., arkworks via WASM)
   * or use the Semaphore protocol if the group context fits.
   */
  async proveLinkage(ctxA: Context, ctxB: Context): Promise<LinkageProof> {
    if (!this.#rootSecret) throw new Error('Identity is locked.');
    // Placeholder proof construction. In production this is replaced with
    // a real Schnorr-equality-of-DL proof; for alpha we emit a deterministic
    // tag that demonstrates the API shape and gates the implementation.
    const a = contextHashHex(ctxA);
    const b = contextHashHex(ctxB);
    const tag = blake3(utf8ToBytes('linkage-proof-placeholder|' + a + '|' + b));
    // Sign the tag with both context keys to demonstrate joint authorship
    // — this leaks the linkage to anyone seeing the proof, which is the
    // *intent* of an opt-in disclosure. It is NOT zero-knowledge; that's
    // the beta upgrade.
    const sigA = await this.sign(tag, ctxA);
    const sigB = await this.sign(tag, ctxB);
    const proof = new Uint8Array(sigA.length + sigB.length);
    proof.set(sigA, 0);
    proof.set(sigB, sigA.length);
    return {
      ctxA: a,
      ctxB: b,
      proof,
      zeroKnowledge: false, // alpha: explicit joint-sig, not ZK. beta: true.
    };
  }

  async lock(): Promise<void> {
    if (this.#rootSecret) {
      this.#rootSecret.fill(0);
      this.#rootSecret = null;
    }
    for (const v of this.#ctxCache.values()) {
      v.secret.fill(0);
    }
    this.#ctxCache.clear();
  }

  fingerprint(): string {
    return this.#rootFingerprint;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// FACTORY
// ─────────────────────────────────────────────────────────────────────────────

export const HierarchicalIdentityFactory: IdentityFactory = {
  async fromSeed(seed: string, password: string): Promise<SignerIdentity> {
    if (!/^[0-9a-f]{64}$/i.test(seed)) {
      throw new Error('Invalid seed format (expected 64 hex chars)');
    }
    const rootSecret = await deriveRootSecret(seed, password);
    return new HierarchicalIdentityImpl(rootSecret);
  },

  async generate(password: string): Promise<{ identity: SignerIdentity; seed: string }> {
    const seed = generateSeedPhrase();
    const identity = await HierarchicalIdentityFactory.fromSeed(seed, password);
    return { identity, seed };
  },

  async fromBackup(_blob: Bytes, _password: string): Promise<SignerIdentity> {
    // TODO(beta): same envelope format as global, but root_secret is the
    // sealed payload (not a flat secret key).
    throw new Error('fromBackup: not implemented in alpha.');
  },
};

export { HierarchicalIdentityImpl };
